import { redirect } from 'react-router-dom';

// Cross-tab authentication synchronization
const AUTH_SYNC_KEY = 'auth_sync_timestamp';
const LOGOUT_EVENT_KEY = 'auth_logout_event';
const LOGOUT_EVENT_TIMEOUT = 2000; // 2 seconds

export function getToken(type: 'access' | 'reset' = 'access') {
  // Note: 'refresh' type removed - refresh tokens are now in HttpOnly cookies

  // Prefer sessionStorage (current tab), fallback to localStorage for cross-tab persistence
  let token = localStorage.getItem(type);
  // fallback to sessionStorage only if not in localStorage
  if (!token) {
    token = sessionStorage.getItem(type);
  }

  if (token) return JSON.parse(token) as string;

  // Handle one-time reset token (kept only in sessionStorage)
  if (type === 'access') {
    const resetToken = sessionStorage.getItem('reset');
    if (resetToken) {
      sessionStorage.removeItem('reset');
      return JSON.parse(resetToken) as string;
    }
  }

  return null;
}

export function setTokens(accessToken: string, refreshToken?: string) {
  // Note: refreshToken parameter kept for backward compatibility but is no longer stored
  // Refresh tokens are now handled via HttpOnly cookies on the backend

  const access = JSON.stringify(accessToken);

  // Only persist access token to both storage types
  sessionStorage.setItem('access', access);
  localStorage.setItem('access', access);

  // Update auth sync timestamp to indicate active session
  const timestamp = Date.now().toString();
  localStorage.setItem(AUTH_SYNC_KEY, timestamp);
  sessionStorage.setItem(AUTH_SYNC_KEY, timestamp);

  // Clear any existing logout events when setting new tokens
  localStorage.removeItem(LOGOUT_EVENT_KEY);
}

export function setResetToken(resetToken: string) {
  sessionStorage.setItem('reset', JSON.stringify(resetToken));
}

export function removeTokens() {
  // Note: Only remove access token - refresh tokens are in HttpOnly cookies
  localStorage.removeItem('access');
  sessionStorage.removeItem('access');

  // Clear auth sync timestamp
  localStorage.removeItem(AUTH_SYNC_KEY);
  sessionStorage.removeItem(AUTH_SYNC_KEY);

  // Trigger logout event for other tabs
  const logoutEvent = {
    timestamp: Date.now(),
    tabId: generateTabId(),
  };
  localStorage.setItem(LOGOUT_EVENT_KEY, JSON.stringify(logoutEvent));

  // Clean up logout event after timeout
  setTimeout(() => {
    // Only remove if it's still our event
    const currentEvent = localStorage.getItem(LOGOUT_EVENT_KEY);
    if (currentEvent) {
      try {
        const event = JSON.parse(currentEvent);
        if (event.tabId === logoutEvent.tabId) {
          localStorage.removeItem(LOGOUT_EVENT_KEY);
        }
      } catch (error) {
        // Invalid event, remove it
        localStorage.removeItem(LOGOUT_EVENT_KEY);
      }
    }
  }, LOGOUT_EVENT_TIMEOUT);
}

// Generate unique tab ID for logout event tracking
function generateTabId(): string {
  if (!sessionStorage.getItem('tab_id')) {
    const tabId = `tab_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    sessionStorage.setItem('tab_id', tabId);
  }
  return sessionStorage.getItem('tab_id') || 'unknown';
}

// Check if current tab should be logged out due to logout in another tab
export function checkCrossTabLogout(): boolean {
  const logoutEvent = localStorage.getItem(LOGOUT_EVENT_KEY);
  if (!logoutEvent) return false;

  try {
    const event = JSON.parse(logoutEvent);
    const currentTabId = sessionStorage.getItem('tab_id');

    // If this logout event is from another tab, we should logout
    if (event.tabId !== currentTabId) {
      // Check if the event is still recent (within timeout)
      const eventAge = Date.now() - event.timestamp;
      if (eventAge < LOGOUT_EVENT_TIMEOUT) {
        return true;
      }
      // Event is too old, clean it up
      localStorage.removeItem(LOGOUT_EVENT_KEY);
      return false;
    }
  } catch (error) {
    // Invalid logout event, clean it up
    localStorage.removeItem(LOGOUT_EVENT_KEY);
  }

  return false;
}

// Get the timestamp of the last auth sync
export function getLastAuthSync(): number | null {
  const timestamp =
    localStorage.getItem(AUTH_SYNC_KEY) ||
    sessionStorage.getItem(AUTH_SYNC_KEY);
  return timestamp ? parseInt(timestamp, 10) : null;
}

// Check if the current tab's auth session is still valid
export function isAuthSessionValid(): boolean {
  const lastSync = getLastAuthSync();
  if (!lastSync) return false;

  // Check if we have valid access token (refresh token is in HttpOnly cookie)
  const accessToken = getToken('access');

  return !!accessToken;
}

// Clear any stale logout events (useful for debugging)
export function clearStaleLogoutEvents(): void {
  const logoutEvent = localStorage.getItem(LOGOUT_EVENT_KEY);
  if (logoutEvent) {
    try {
      const event = JSON.parse(logoutEvent);
      const eventAge = Date.now() - event.timestamp;
      if (eventAge > LOGOUT_EVENT_TIMEOUT) {
        localStorage.removeItem(LOGOUT_EVENT_KEY);
      }
    } catch (error) {
      localStorage.removeItem(LOGOUT_EVENT_KEY);
    }
  }
}

// Debug function to reset cross-tab authentication state (useful for troubleshooting)
export function resetCrossTabAuthState(): void {
  localStorage.removeItem(LOGOUT_EVENT_KEY);
  localStorage.removeItem(AUTH_SYNC_KEY);
  sessionStorage.removeItem(AUTH_SYNC_KEY);
  // console.log('Cross-tab authentication state reset');
}

// Comprehensive debug function to show current auth state
export function debugAuthState(): void {
  // console.log('=== AUTH STATE DEBUG ===');
  // console.log('Access Token:', getToken('access') ? 'EXISTS' : 'MISSING');
  // console.log('Refresh Token:', getToken('refresh') ? 'EXISTS' : 'MISSING');
  // console.log('Auth Sync Timestamp:', getLastAuthSync());
  // console.log('Session Valid:', isAuthSessionValid());

  const logoutEvent = localStorage.getItem(LOGOUT_EVENT_KEY);
  if (logoutEvent) {
    try {
      // const event = JSON.parse(logoutEvent);
      // console.log('Logout Event:', {
      //   timestamp: event.timestamp,
      //   age: Date.now() - event.timestamp,
      //   tabId: event.tabId,
      //   currentTabId: sessionStorage.getItem('tab_id'),
      // });
    } catch (error) {
      // console.log('Invalid Logout Event:', logoutEvent);
    }
  } else {
    // console.log('No Logout Event');
  }

  // console.log('Current Tab ID:', sessionStorage.getItem('tab_id'));
  // console.log('========================');
}

export function checkAuthLoader() {
  const token = getToken();

  if (!token) {
    return redirect('/login');
  }

  return new Response(JSON.stringify({ status: true }));
}

// ------------------------------------------------------------
// Cross-tab refresh lock helper
// ------------------------------------------------------------

/**
 * Ensures that a critical section (like calling /auth/refresh-tokens) is
 * executed by only one tab at a time. Other tabs wait until the lock is
 * released, then simply continue without running the same action.
 *
 * Usage:
 *   await withRefreshLock(async () => {
 *     // call backend, update tokens, etc.
 *   });
 */
export async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const LOCK_KEY = 'refreshLock';
  const STALE_AFTER_MS = 10_000; // consider lock stale after 10s

  const acquireLock = async (): Promise<void> => {
    while (true) {
      const existing = localStorage.getItem(LOCK_KEY);
      const now = Date.now();

      if (!existing || now - Number(existing) > STALE_AFTER_MS) {
        // acquire
        try {
          localStorage.setItem(LOCK_KEY, String(now));
        } catch {
          /* ignore quota errors */
        }
        // double-check to ensure we own the lock
        const confirm = localStorage.getItem(LOCK_KEY);
        if (confirm === String(now)) return;
      }
      // wait 300 ms then retry
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 300);
      });
    }
  };

  const releaseLock = (): void => {
    localStorage.removeItem(LOCK_KEY);
  };

  try {
    await acquireLock();
    return await fn();
  } finally {
    releaseLock();
  }
}
